import '../global.css';
import 'react-native-reanimated';
import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider,
} from '@react-navigation/native';
import * as SplashScreen from 'expo-splash-screen';
import { SplashScreenProvider } from '@/features/global/contexts/splash-screen/SplashScreen';
import { AuthProvider } from '~/auth/contexts/auth/Auth';
import { PurchasesProvider } from '~/purchases/contexts/purchases/Purchases';
import { useColorScheme } from '@/hooks/useColorScheme';
import { Slot } from 'expo-router';
import { I18nProvider } from '~/i18n/contexts/i18n/I18n';
import { PortalHost } from '@rn-primitives/portal';
import { setDefaultOptions } from 'date-fns';
import { ApolloProvider } from '@apollo/client/react';
import { apolloClient } from '@/lib/apollo';
import { Helmet } from 'react-helmet';
import { KeyboardAvoidingView, Platform, View } from 'react-native';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { PowerSyncProvider } from '~/powersync/contexts/power-sync/PowerSync';
import { HealthContextProvider } from '~/health/contexts/health/Health';
import { ModalProvider } from '@/contexts/ModalContext';
import { ToastProvider } from '~/global/contexts/toast/Toast';
import { FliptProvider } from '~/flipt/contexts/flipt/Flipt';
import * as Sentry from '@sentry/react-native';
import { createSentryInitOptions } from '@fytness/shared';
import { UpdateCheckerWrapper } from '~/global/components/UpdateChecker';
import { StatusBar } from 'expo-status-bar';

Sentry.init({
  ...createSentryInitOptions({
    dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
    environment: process.env.NODE_ENV,
  }),
  integrations: [
    Sentry.mobileReplayIntegration(),
    Sentry.feedbackIntegration(),
  ],

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

export { ErrorBoundary } from 'expo-router';

setDefaultOptions({
  weekStartsOn: 1,
});

void SplashScreen.preventAutoHideAsync();

export default Sentry.wrap(function RootLayout() {
  const { colorScheme } = useColorScheme();
  return (
    <>
      <Helmet>
        <title>fytness</title>
      </Helmet>
      <StatusBar style={colorScheme === 'dark' ? 'light' : 'dark'} />
      <I18nProvider>
        <ApolloProvider client={apolloClient}>
          <AuthProvider>
            <PurchasesProvider>
              <ThemeProvider
                value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}
              >
                <FliptProvider>
                  <PowerSyncProvider>
                    <HealthContextProvider>
                      <SplashScreenProvider>
                        <SafeAreaProvider>
                          {/* ToastProvider wraps ModalProvider so modals (rendered
                            inside ModalProvider) can call useToast(). */}
                          <ToastProvider>
                            <ModalProvider>
                              <UpdateCheckerWrapper />
                              <View style={{ flex: 1 }}>
                                <SafeAreaView
                                  className="flex-1"
                                  edges={['top']}
                                >
                                  <KeyboardAvoidingView
                                    behavior={
                                      Platform.OS === 'ios'
                                        ? 'padding'
                                        : 'height'
                                    }
                                    style={{ flex: 1 }}
                                  >
                                    <Slot />
                                  </KeyboardAvoidingView>
                                  <PortalHost />
                                </SafeAreaView>
                              </View>
                            </ModalProvider>
                          </ToastProvider>
                        </SafeAreaProvider>
                      </SplashScreenProvider>
                    </HealthContextProvider>
                  </PowerSyncProvider>
                </FliptProvider>
              </ThemeProvider>
            </PurchasesProvider>
          </AuthProvider>
        </ApolloProvider>
      </I18nProvider>
    </>
  );
});
